"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { getDeckCardsMap } from "@/lib/games/onePiece/DeckCommonCardsFilter";
import { Spinner } from "@heroui/react";
import {
  setCurrentGameAll,
  importDeckString,
  useFilters,
  useSetFilter,
  useClearAllFilters,
  useSetFilters,
  usePage,
  useSetPage,
  useSearchDisplay,
  useSetSearchDisplay,
  useHoveredAACardId,
  useSetHoveredAACardId,
  useModalState,
  useOpenCardModal,
  useCloseCardModal,
  useSetModalCardIndex,
  useGetCount,
  useCopyDeck,
  useClearNewDeck,
  useGetDecklist,
  useGetLeaderId,
  useAddCard,
  useSetCard,
  useDeckCounts,
  useDeckStore,
  EMPTY_DECK_ITEMS,
} from "@/stores/deckStore";

import { IDecklistV2 } from "@/models/decklist";
import GenericCardModalWithNavigation from "@/components/CardModal/GenericCardModalWithNavigation";
import "./deckbuilder.css";
import View from "@/components/DeckBuilder/Mobile/View";
import DeckLayoutLeftSide from "@/components/DeckBuilder/DeckLayoutLeftSide";
import DeckLayoutBottom from "@/components/DeckBuilder/DeckLayoutBottom";
import DeckLayoutMax from "@/components/DeckBuilder/DeckLayoutMax";
import isEqual from "lodash/isEqual";
import {
  DndContext,
  DragOverlay,
  useSensors,
  useSensor,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import { useClientMediaQuery } from "@/lib/hooks/isMobile";
import { useQueryState, parseAsString } from "nuqs";
import LAYOUTS from "@/components/DeckBuilder/layouts";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import { CardItem, RBItem } from "@/interfaces/CardItem";

import SharedDisplayCard, {
  CardImage as SharedCardImage,
} from "@/components/DeckBuilder/shared/DisplayCard";

interface GenericDeckBuilderProps {
  game: SupportedGames;
  decks: IDecklistV2[];
}

export default function GenericDeckBuilder({
  game,
  decks,
}: GenericDeckBuilderProps) {
  const handler = getGameHandler(game);
  const enableRotation = handler.isRotatable
    ? (card: CardItem) => handler.isRotatable!(card)
    : undefined;
  const cardFunctions = handler.getDeckBuilderCardFunctions();
  const {
    getCardData,
    getAltArts,
    getAACardId,
    getAACardIdData,
    cleanCardData,
  } = cardFunctions;

  // Get state from store
  const modalState = useModalState();
  const openCardModal = useOpenCardModal();
  const closeCardModal = useCloseCardModal();
  const setModalCardIndex = useSetModalCardIndex();

  const hoveredAACardId = useHoveredAACardId();
  const setHoveredAACardId = useSetHoveredAACardId();
  // Keep the latest hovered card in a ref so the global keydown handler
  // (which is only re-attached on a few deps) never reads a stale value.
  const hoveredAACardIdRef = useRef<string | null>(hoveredAACardId);
  hoveredAACardIdRef.current = hoveredAACardId;
  const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const filters = useFilters();
  const setFilter = useSetFilter();
  const clearAllFiltersBase = useClearAllFilters();
  const setFilters = useSetFilters();

  const page = usePage();
  const setPage = useSetPage();

  const searchDisplay = useSearchDisplay();
  const setSearchDisplay = useSetSearchDisplay();

  const getCount = useGetCount();
  const copyDeck = useCopyDeck();
  const clearNewDeck = useClearNewDeck();

  const addCard = useAddCard();
  const setCard = useSetCard();

  const deckCounts = useDeckCounts();

  const [activeId, setActiveId] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Set the current game in all stores when the component mounts or game changes
  useEffect(() => {
    setCurrentGameAll(game);
  }, [game]);

  // Initialize filters from handler if empty - memoize to prevent recreating on every render
  const defaultFilters = useMemo(() => handler.getDefaultFilters(), [handler]);

  // Wrap clearAllFilters to automatically pass defaultFilters
  const clearAllFilters = useCallback(() => {
    clearAllFiltersBase(defaultFilters);
  }, [clearAllFiltersBase, defaultFilters]);

  // Use Zustand store for deck state
  // The fallback must be a stable reference: a fresh [] here reads as a new
  // value on every render and zustand loops. See EMPTY_DECK_ITEMS.
  const deckItems = useDeckStore(
    (state) => state.decks[game] || EMPTY_DECK_ITEMS,
  );

  // Initialize or validate filters
  useEffect(() => {
    // Check if filters need initialization or if they have invalid Set values
    const needsInit =
      Object.keys(filters).length === 0 ||
      Object.values(filters).some(
        (val) =>
          val &&
          typeof val === "object" &&
          !(val instanceof Set) &&
          val.constructor === Object &&
          Object.keys(val).length === 0,
      );

    if (needsInit) {
      setFilters(defaultFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync cardOrderIds with deck items
  useEffect(() => {
    const deckIdsSet = new Set(deckItems.map((item) => item.id));
    const currentOrderIds = useDeckStore.getState().cardOrderIds[game] || [];

    // Filter out ids that no longer exist in the deck
    const filteredIds = currentOrderIds.filter((id) => deckIdsSet.has(id));

    // Add new ids from the deck that are not already in cardOrderIds
    const newIds = deckItems
      .map((item) => item.id)
      .filter((id) => !filteredIds.includes(id));

    // Only update if there are changes
    if (newIds.length > 0 || filteredIds.length !== currentOrderIds.length) {
      const updatedIds = [...filteredIds, ...newIds];
      useDeckStore.getState().setCardOrderIds(game, updatedIds);
    }
  }, [deckItems, game]);

  let modalCardData = modalState.aaCardId
    ? handler.getModalCardData(modalState.aaCardId)
    : undefined;

  const handleOpen = (aaCardId: string) => {
    // Find the index of the card in the filtered cards list
    const cardIndex = filteredCards.findIndex((card) => {
      const aa = getAltArts(card.id, filters.showAAs);
      return aa.some((aaItem: any) => getAACardId(aaItem) === aaCardId);
    });
    openCardModal(aaCardId, cardIndex);
  };

  // Minimal deck object for Export/Stats (legacy compatibility)
  const getDecklist = useGetDecklist();
  const getLeaderId = useGetLeaderId(handler);
  const deck = useMemo(() => {
    return {
      getDecklist,
      getLeaderId,
    };
  }, [getDecklist, getLeaderId]);

  const searchParams = useSearchParams();
  const [layout, setLayout] = useQueryState(
    "layout",
    parseAsString.withDefault(LAYOUTS.DECK),
  );
  const [viewMode, setViewMode] = useQueryState(
    "d",
    parseAsString.withDefault(""),
  );
  const router = useRouter();
  const pathname = usePathname();

  const isMobile = useClientMediaQuery("(max-width: 600px)");

  function clearDeckSearchParam() {
    // Create a URL object based on the current path
    const params = new URLSearchParams(searchParams.toString());
    // Delete the 'deck' query parameter
    params.delete("deck");

    // Construct the new URL
    const newUrl = params.toString()
      ? `${pathname}?${params.toString()}`
      : pathname;

    // Push the new URL to the router
    router.push(newUrl);
  }

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filters, layout]);

  useEffect(() => {
    // debounce
    const timeout = setTimeout(() => {
      setFilter("search", searchDisplay);
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchDisplay]);

  useEffect(() => {
    let deckString = "";
    if (searchParams.has("deck")) {
      deckString = searchParams.get("deck") || "";
    }
    if (deckString !== "") {
      importDeckString(game, deckString);
    }
    clearDeckSearchParam();
  }, [searchParams, game]);

  const deckCardsMap = getDeckCardsMap(decks);

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    if (event.over?.id === "display-deck") {
      addCard(event.active.id, 1);
    }
    setActiveId(null);
  }

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 1,
    },
  });
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor,
    pointerSensor,
  );

  const baseSortedCards: CardItem[] = React.useMemo(() => {
    const allCards = Object.values(cleanCardData()) as CardItem[];
    console.log(
      "[TEST] EB05 cards in base list:",
      allCards.filter((c: any) => c.id?.startsWith("EB05")).length,
    );
    return allCards.sort((a, b) => handler.sortCards(a, b));
  }, [handler]);

  const filteredCards: CardItem[] = React.useMemo(() => {
    let filtered: CardItem[] = [];

    if (isEqual(filters, defaultFilters)) {
      console.log(
        "[TEST] Using default filters, returning all",
        baseSortedCards.length,
        "cards",
      );
      return baseSortedCards;
    }

    filtered = handler.filterCards(
      baseSortedCards,
      filters,
      defaultFilters,
      deckCardsMap,
    );
    console.log(
      "[TEST] EB05 cards after filter:",
      filtered.filter((c: any) => c.id?.startsWith("EB05")).length,
    );
    return filtered;
  }, [filters, baseSortedCards, handler, defaultFilters, deckCardsMap]);

  // Navigation functions for modal
  const navigateToCard = (direction: "prev" | "next") => {
    if (modalState.currentCardIndex === -1) return;

    let newIndex =
      direction === "prev"
        ? modalState.currentCardIndex - 1
        : modalState.currentCardIndex + 1;

    // Handle wrapping around the list
    if (newIndex < 0) {
      newIndex = filteredCards.length - 1;
    } else if (newIndex >= filteredCards.length) {
      newIndex = 0;
    }

    const targetCard = filteredCards[newIndex];
    const aa = getAltArts(targetCard.id, filters.showAAs);
    const newAaCardId = getAACardId(aa[0]); // Use the first alt art

    openCardModal(newAaCardId, newIndex);
  };

  // Track mouse position globally
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY };
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Consolidated keyboard event handler
  useEffect(() => {
    // Resolve the card the cursor is currently over (works in every layout,
    // both the card DB grid and the cards already in the deck)
    const getCardIdUnderCursor = (): string | null => {
      const { x, y } = mousePositionRef.current;
      const element = document.elementFromPoint(x, y);
      const cardElement = element?.closest("[data-id]") as HTMLElement | null;
      return cardElement?.getAttribute("data-id") || null;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Modal-specific shortcuts (when modal is open)
      if (modalState.isOpen) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          navigateToCard("prev");
          return;
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          navigateToCard("next");
          return;
        }
        // Don't process other shortcuts when modal is open
        return;
      }

      // General shortcuts (when modal is closed)
      if (event.key === "ArrowLeft") {
        setPage(Math.max(page - 1, 1));
        return;
      }

      if (event.key === "ArrowRight") {
        const maxPage = Math.ceil(filteredCards.length / cardsPerPage);
        setPage(Math.min(page + 1, maxPage));
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.code === "KeyK") {
        event.preventDefault();
        const inputElement = document.getElementById("search-filter");
        inputElement?.focus();
        return;
      }

      if (
        event.key === "i" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        const cardId = getCardIdUnderCursor() ?? hoveredAACardIdRef.current;
        if (cardId) {
          const activeElement = document?.activeElement as HTMLElement | null;
          activeElement?.blur();
          setHoveredAACardId(cardId);
          handleOpen(cardId);
        }
        return;
      }

      if (
        ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(event.key)
      ) {
        // Check what card is under the cursor right now, falling back to the
        // last hovered card
        const cardId = getCardIdUnderCursor() ?? hoveredAACardIdRef.current;

        if (cardId) {
          const activeElement = document?.activeElement as HTMLElement | null;
          activeElement?.blur();
          setHoveredAACardId(cardId); // Update state
          setCard(cardId, parseInt(event.key));
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [page, modalState.isOpen, modalState.currentCardIndex, filteredCards]);

  let cardsPerPage = handler.getCardsPerPage();

  const hasNextPage = React.useCallback(() => {
    return cardsPerPage * page < filteredCards.length;
  }, [cardsPerPage, page, filteredCards.length]);

  const fetchNextPage = React.useCallback(() => {
    setPage((p) => p + 1);
  }, [setPage]);

  const displayCards = useMemo(() => {
    let cards = [];

    if (filteredCards.length > 0) {
      for (
        let i = 0;
        i < Math.min(page * cardsPerPage, filteredCards.length);
        i++
      ) {
        const curCardId = filteredCards[i].id;
        let cardData = getCardData(curCardId);
        const aa = getAltArts(curCardId, filters.showAAs);
        for (let j = 0; j < aa.length; j++) {
          const curAA = aa[j];
          const aaCardId = getAACardId(curAA);
          const cardCount = deckCounts.get(aaCardId) || 0;
          cards.push(
            <SharedDisplayCard
              key={`${aaCardId}-${cardCount}`}
              aaCardId={aaCardId}
              cardData={cardData}
              handleOpen={handleOpen}
              setHoveredAACardId={setHoveredAACardId}
              cardCount={cardCount}
              handler={handler}
            />,
          );
        }
      }
    }
    return cards;
  }, [
    filteredCards,
    page,
    deckCounts,
    filters.showAAs,
    handler,
    getCardData,
    getAltArts,
    getAACardId,
    addCard,
    setCard,
  ]);

  let view;

  if (isMobile) {
    view = (
      <View
        searchDisplay={searchDisplay}
        setSearchDisplay={setSearchDisplay}
        deck={deck}
        getCount={getCount}
        copyDeck={copyDeck}
        filters={filters}
        setFilter={setFilter}
        clearAllFilters={clearAllFilters}
        deckCardsMap={deckCardsMap}
        displayCards={displayCards}
        handleOpen={handleOpen}
        setLayout={setLayout}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        page={page}
        clearNewDeck={clearNewDeck}
        setHoveredAACardId={setHoveredAACardId}
        setPage={setPage}
        layout={layout}
      />
    );
  } else {
    if (layout === "side") {
      view = (
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          autoScroll={false}
        >
          <DeckLayoutLeftSide
            deck={deck}
            getCount={getCount}
            copyDeck={copyDeck}
            filters={filters}
            setFilter={setFilter}
            clearAllFilters={clearAllFilters}
            deckCardsMap={deckCardsMap}
            displayCards={displayCards}
            handleOpen={handleOpen}
            setLayout={setLayout}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            page={page}
            clearNewDeck={clearNewDeck}
          />
          <DragOverlay dropAnimation={null}>
            {activeId ? (
              <SharedCardImage aaCardId={activeId} handler={handler} />
            ) : null}
          </DragOverlay>
        </DndContext>
      );
    } else if (layout === LAYOUTS.EDIT) {
      view = (
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          autoScroll={false}
        >
          <DeckLayoutBottom
            viewMode={viewMode}
            deck={deck}
            searchDisplay={searchDisplay}
            setSearchDisplay={setSearchDisplay}
            getCount={getCount}
            copyDeck={copyDeck}
            filters={filters}
            setFilter={setFilter}
            clearAllFilters={clearAllFilters}
            deckCardsMap={deckCardsMap}
            displayCards={displayCards}
            handleOpen={handleOpen}
            setLayout={setLayout}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            page={page}
            clearNewDeck={clearNewDeck}
            setHoveredAACardId={setHoveredAACardId}
          />
          <DragOverlay dropAnimation={null}>
            {activeId ? (
              <SharedCardImage aaCardId={activeId} handler={handler} />
            ) : null}
          </DragOverlay>
        </DndContext>
      );
    } else if (LAYOUTS.DECK === layout || LAYOUTS.STATS === layout) {
      view = (
        <DeckLayoutMax
          handleOpen={handleOpen}
          getCount={getCount}
          copyDeck={copyDeck}
          deck={deck}
          setLayout={setLayout}
          setHoveredAACardId={setHoveredAACardId}
        />
      );
    }
  }

  return isClient ? (
    <div>
      {view}
      <GenericCardModalWithNavigation
        isOpen={modalState.isOpen}
        onOpenChange={() => closeCardModal()}
        cardData={modalCardData}
        gameName={game}
        aaCardId={modalState.aaCardId}
        onNavigate={navigateToCard}
        enableRotation={
          enableRotation && modalCardData
            ? enableRotation(modalCardData)
            : false
        }
      />
    </div>
  ) : (
    <div className="w-full h-[500px] flex items-center justify-center">
      <Spinner />
    </div>
  );
}
